/**
 * @fileoverview Assign Target mutation hook.
 * @description Wraps POST /sales/targets in a TanStack mutation: one all-or-nothing batch of backlog
 * prospects for one assignee. Assigning also makes the assignee each prospect's owner and takes the
 * prospects out of the backlog, so every sales query is refreshed once it succeeds. A refusal (a
 * prospect already has a target, the assignee is out of scope, a recent sign-in is needed) rolls
 * the whole batch back and shows as the dialog's error.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { assignTargetBatch } from '@/features/sales/services/sales-targets.service'

import type {
  AssignTargetBatchRequest,
  AssignTargetBatchResult,
} from '@/features/sales/types/sales-target.types'

/**
 * @description Assigns one target per prospect of a batch to one assignee. The command's
 * idempotency key outlives the dialog, so a retry after an unknown outcome replays it.
 * @param {object} [options] - What follows a success.
 * @param {(result: AssignTargetBatchResult) => void} [options.onSuccess] - Called with the saved
 * batch, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with an
 * AssignTargetBatchRequest).
 */
export function useAssignProspectTargets({
  onSuccess,
}: {
  onSuccess?: ((result: AssignTargetBatchResult) => void) | undefined
} = {}) {
  return useIdempotentMutation<AssignTargetBatchRequest, AssignTargetBatchResult>({
    action: 'assign-targets',
    /**
     * @description Assigns the batch of targets under the key it is given.
     * @param {AssignTargetBatchRequest} request - The batch.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<AssignTargetBatchResult>} The result.
     */
    send: (request, key) => assignTargetBatch(request, key),
    onSuccess,
  })
}
